import {
  PropertySearchResults,
  GroupRatesPropertySearchResults,
  GroupRatesPropertySearchResultsDtt,
  GroupsRateProperty,
  GroupsRatePropertyDtt,
  // Search properties queries
  searchPropertiesByGeolocationQuery,
  searchPropertiesByGeolocationDttQuery,
  searchPropertiesByLocationQuery,
  searchPropertiesByLocationDttQuery,
  searchPropertiesByDestinationQuery,
  searchPropertiesByDestinationDttQuery,
  // Search Group Rates queries
  searchGroupRatesByGeolocationQuery,
  searchGroupRatesByGeolocationDttQuery,
  searchGroupRatesByLocationQuery,
  searchGroupRatesByLocationDttQuery,
  searchGroupRatesByDestinationQuery,
  searchGroupRatesByDestinationDttQuery,
  // Available filter queries
  getAvailableFiltersByGeolocationQuery,
  getAvailableFiltersByGeolocationDttQuery,
  getAvailableFiltersByLocationQuery,
  getAvailableFiltersByLocationDttQuery,
  getAvailableFiltersByDestinationQuery,
  getAvailableFiltersByDestinationDttQuery,
  getAvailableFiltersGroupRatesByGeolocationDttQuery,
  getAvailableFiltersGroupRatesByLocationDttQuery,
  getAvailableFiltersGroupRatesByDestinationDttQuery,
} from '@marriott/mi-groups-graphql';
import { useLazyQueryTransformer } from '../../hooks';

type SearchPropertiesByGeolocation = {
  searchPropertiesByGeolocation: PropertySearchResults;
};

type SearchPropertiesByLocation = {
  searchPropertiesByLocation: PropertySearchResults;
};

type SearchPropertiesByDestination = {
  searchPropertiesByDestination: PropertySearchResults;
};

type SearchGroupRatesByGeolocation = {
  searchGroupRatesByGeoLocation: GroupRatesPropertySearchResults;
};

type SearchGroupRatesByLocation = {
  searchGroupRatesByLocation: GroupRatesPropertySearchResults;
};

type SearchGroupRatesByDestination = {
  searchGroupRatesByDestination: GroupRatesPropertySearchResults;
};

// DTT types
type SearchPropertiesByGeolocationDtt = {
  search: { properties: { searchByGeolocation: PropertySearchResults } };
};

type SearchPropertiesByLocationDtt = {
  search: { properties: { searchByLocation: PropertySearchResults } };
};

type SearchPropertiesByDestinationDtt = {
  search: { properties: { searchByDestination: PropertySearchResults } };
};

type SearchGroupRatesByGeolocationDtt = {
  search: { groupRates: { searchByGeolocation: GroupRatesPropertySearchResultsDtt } };
};

type SearchGroupRatesByLocationDtt = {
  search: { groupRates: { searchByLocation: GroupRatesPropertySearchResultsDtt } };
};

type SearchGroupRatesByDestinationDtt = {
  search: { groupRates: { searchByDestination: GroupRatesPropertySearchResultsDtt } };
};

const transformGroupRateEdges = (edges: GroupsRateProperty[]): GroupsRatePropertyDtt[] => {
  return edges.map(edge => ({
    node: { ...edge.node },
    groupRate: {
      isBookingAllowed: edge?.groupRate?.availability?.code === '1',
      eventSpaceRates:
        edge?.groupRate?.eventSpaceRates?.map(eventSpace => ({
          id: eventSpace.id,
          name: eventSpace.name,
          amount: {
            amount: eventSpace.amount.origin.value,
            currency: eventSpace.amount.origin.currency,
            decimalPoint: eventSpace.amount.origin.valueDecimalPoint,
          },
        })) || [],
      roomRates:
        edge?.groupRate?.roomRates?.map(roomRate => ({
          inventory: roomRate.inventory,
          roomType: roomRate.roomType,
          amount: {
            amount: roomRate.amount.origin.value,
            currency: roomRate.amount.origin.currency,
            decimalPoint: roomRate.amount.origin.valueDecimalPoint,
          },
        })) || [],
    },
  }));
};

export const useSearchPropertiesByGeolocation = (useFilters = false) => {
  return useLazyQueryTransformer<SearchPropertiesByGeolocation, SearchPropertiesByGeolocationDtt>({
    currentQuery: useFilters ? getAvailableFiltersByGeolocationQuery : searchPropertiesByGeolocationQuery,
    dttQuery: useFilters ? getAvailableFiltersByGeolocationDttQuery : searchPropertiesByGeolocationDttQuery,
    onTransform: data => {
      return {
        search: {
          properties: {
            searchByGeolocation: data.searchPropertiesByGeolocation,
          },
        },
      };
    },
  });
};

export const useSearchPropertiesByLocation = (useFilters = false) => {
  return useLazyQueryTransformer<SearchPropertiesByLocation, SearchPropertiesByLocationDtt>({
    currentQuery: useFilters ? getAvailableFiltersByLocationQuery : searchPropertiesByLocationQuery,
    dttQuery: useFilters ? getAvailableFiltersByLocationDttQuery : searchPropertiesByLocationDttQuery,
    onTransform: data => {
      return {
        search: {
          properties: {
            searchByLocation: data.searchPropertiesByLocation,
          },
        },
      };
    },
  });
};

export const useSearchPropertiesByDestination = (useFilters = false) => {
  return useLazyQueryTransformer<SearchPropertiesByDestination, SearchPropertiesByDestinationDtt>({
    currentQuery: useFilters ? getAvailableFiltersByDestinationQuery : searchPropertiesByDestinationQuery,
    dttQuery: useFilters ? getAvailableFiltersByDestinationDttQuery : searchPropertiesByDestinationDttQuery,
    onTransform: data => {
      return {
        search: {
          properties: {
            searchByDestination: data.searchPropertiesByDestination,
          },
        },
      };
    },
  });
};

export const useSearchGroupRatesByGeolocation = (useFilters = false) => {
  return useLazyQueryTransformer<SearchGroupRatesByGeolocation, SearchGroupRatesByGeolocationDtt>({
    currentQuery: searchGroupRatesByGeolocationQuery,
    dttQuery: useFilters ? getAvailableFiltersGroupRatesByGeolocationDttQuery : searchGroupRatesByGeolocationDttQuery,
    onTransform: data => {
      const { total, edges, facets, pageInfo } = data?.searchGroupRatesByGeoLocation || {};

      return {
        search: {
          groupRates: {
            searchByGeolocation: {
              total,
              edges: transformGroupRateEdges(edges),
              facets,
              pageInfo,
            },
          },
        },
      };
    },
    selectDttFlag: s => s.isDttSwpGroupsEnabled || s.isSwp,
  });
};

export const useSearchGroupRatesByLocation = (useFilters = false) => {
  return useLazyQueryTransformer<SearchGroupRatesByLocation, SearchGroupRatesByLocationDtt>({
    currentQuery: searchGroupRatesByLocationQuery,
    dttQuery: useFilters ? getAvailableFiltersGroupRatesByLocationDttQuery : searchGroupRatesByLocationDttQuery,
    onTransform: data => {
      const { total, edges, facets, pageInfo } = data?.searchGroupRatesByLocation || {};

      return {
        search: {
          groupRates: {
            searchByLocation: {
              total,
              edges: transformGroupRateEdges(edges),
              facets,
              pageInfo,
            },
          },
        },
      };
    },
    selectDttFlag: s => s.isDttSwpGroupsEnabled || s.isSwp,
  });
};

export const useSearchGroupRatesByDestination = (useFilters = false) => {
  return useLazyQueryTransformer<SearchGroupRatesByDestination, SearchGroupRatesByDestinationDtt>({
    currentQuery: searchGroupRatesByDestinationQuery,
    dttQuery: useFilters ? getAvailableFiltersGroupRatesByDestinationDttQuery : searchGroupRatesByDestinationDttQuery,
    onTransform: data => {
      const { total, edges, facets, searchCenter, pageInfo } = data?.searchGroupRatesByDestination || {};

      return {
        search: {
          groupRates: {
            searchByDestination: {
              total,
              edges: transformGroupRateEdges(edges),
              facets,
              searchCenter,
              pageInfo,
            },
          },
        },
      };
    },
    selectDttFlag: s => s.isDttSwpGroupsEnabled || s.isSwp,
  });
};
